import { Box, Button, Checkbox, Form, FormField, Input, SelectA11y, TextArea } from '@base-app';
import { CardGrid, Direction } from '@builder';
import {
  StyledFormFieldsContainer,
  StyledFormSectionContainer,
  StyledParagraph,
  cardGridStyles,
  withdrawalLabelStyles,
  cardLabelStyles,
  cardBoldLabelStyles,
  leftFormFieldStyles,
  rightFormFieldStyles,
  rowFormFieldStyles,
  columnFormFieldStyles,
  submitButtonStyles,
} from './WithdrawalForm.styles';
import { Sizes, Variants } from '@/common/types';
import { useTranslation } from 'react-i18next';
import { WITHDRAWAL_TRANSLATION } from '../../constants/common';
import { reasonsForWithdrawalProps, WithdrawalFormProps, WithdrawalFormValues } from './WithdrawalForm.types';
import { WithdrawalReasonOptions } from '../../constants/lists/withdrawalReasons';
import { FC, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useWithdrawalValidation } from '../../hooks/useWithdrawalValidation';
import { useWithdrawalOptions } from '../../hooks/useWithdrawalOptions';

const WithdrawalForm: FC<WithdrawalFormProps> = ({ handleFormSubmit, isLoading }) => {
  const { t } = useTranslation(WITHDRAWAL_TRANSLATION);
  const { typeOfRequestOptions, hrnSampleStatusOptions, paperConsentOptions } = useWithdrawalOptions();

  const [documentationConfirmationCheck, setDocumentationConfirmationCheck] = useState(false);
  const [orderCancellationCheck, setOrderCancellationCheck] = useState(false);
  const defaultReasonsForWithdrawal: reasonsForWithdrawalProps = {
    privacyConcerns: false,
    distanceFromLab: false,
    insuranceConcerns: false,
    unableToProvideSample: false,
    piRemovalDueToIneligibility: false,
    passedAwayPriorToSampleCollection: false,
    removedDueTo3xTnps: false,
    other: false,
    reasonNotSpecified: false,
  };
  const schema = useWithdrawalValidation(typeOfRequestOptions, hrnSampleStatusOptions, paperConsentOptions);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<WithdrawalFormValues>({
    defaultValues: {
      reasonsForWithdrawal: defaultReasonsForWithdrawal,
    },
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const handleSubmitButtonClick: SubmitHandler<WithdrawalFormValues> = (values): void => {
    handleFormSubmit(values);
  };

  return (
    <>
      <Form onSubmit={handleSubmit(handleSubmitButtonClick)} data-testid="withdrawal-form">
        <FormField css={withdrawalLabelStyles}>{t('form.withdrawal_message')}</FormField>

        <CardGrid css={cardGridStyles} direction={Direction.ROW} title={t('form.research_participant_s.title')}>
          <StyledFormSectionContainer>
            <Box css={cardLabelStyles}>
              <StyledParagraph>{t('form.research_participant_s.subtitle')}</StyledParagraph>
            </Box>

            <StyledFormFieldsContainer>
              <FormField css={leftFormFieldStyles}>
                <Controller
                  control={control}
                  name="participantDateOfBirth"
                  render={({ field }) => (
                    <Input
                      {...field}
                      error={!!errors.participantDateOfBirth}
                      errorDescription={errors.participantDateOfBirth?.message}
                      fullWidth
                      id={field.name}
                      label={t('form.research_participant_s.research_participant_s_date_of_birth')}
                      secondaryLabel={t('form.required')}
                      name={field.name}
                      placeholder={t('form.date_of_birth_example')}
                      testId="withdrawal-form-input-date-of-birth"
                      isSensitive={true}
                    />
                  )}
                />
              </FormField>
              <FormField css={rightFormFieldStyles}>
                <Controller
                  control={control}
                  name="participantMRN"
                  render={({ field }) => (
                    <Input
                      {...field}
                      error={!!errors.participantMRN}
                      errorDescription={errors.participantMRN?.message}
                      fullWidth
                      id={field.name}
                      label={t('form.research_participant_s.research_participant_s_mrn')}
                      secondaryLabel={t('form.required')}
                      name={field.name}
                      placeholder={t('form.mrn_example')}
                      testId="withdrawal-form-input-mrn"
                      isSensitive={true}
                    />
                  )}
                />
              </FormField>
            </StyledFormFieldsContainer>
            <StyledFormFieldsContainer>
              <FormField css={leftFormFieldStyles}>
                <Controller
                  control={control}
                  name="participantFirstName"
                  render={({ field }) => (
                    <Input
                      {...field}
                      error={!!errors.participantFirstName}
                      errorDescription={errors.participantFirstName?.message}
                      fullWidth
                      id={field.name}
                      label={t('form.research_participant_s.research_participant_s_first_name')}
                      secondaryLabel={t('form.required')}
                      name={field.name}
                      placeholder={t('form.first_name_example')}
                      testId="withdrawal-form-input-first-name"
                      isSensitive={true}
                    />
                  )}
                />
              </FormField>
              <FormField css={rightFormFieldStyles}>
                <Controller
                  control={control}
                  name="participantLastName"
                  render={({ field }) => (
                    <Input
                      {...field}
                      error={!!errors.participantLastName}
                      errorDescription={errors.participantLastName?.message}
                      fullWidth
                      id={field.name}
                      label={t('form.research_participant_s.research_participant_s_last_name')}
                      secondaryLabel={t('form.required')}
                      name={field.name}
                      placeholder={t('form.last_name_example')}
                      testId="withdrawal-form-input-last-name"
                      isSensitive={true}
                    />
                  )}
                />
              </FormField>
            </StyledFormFieldsContainer>

            <StyledFormFieldsContainer>
              <FormField css={leftFormFieldStyles}>
                <Controller
                  control={control}
                  name="participantEmail"
                  render={({ field }) => (
                    <Input
                      {...field}
                      error={!!errors.participantEmail}
                      errorDescription={errors.participantEmail?.message}
                      fullWidth
                      id={field.name}
                      label={t('form.research_participant_s.research_participant_s_email_address')}
                      secondaryLabel={t('form.required')}
                      name={field.name}
                      placeholder={t('form.email_address_example')}
                      testId="withdrawal-form-input-email-address"
                      isSensitive={true}
                    />
                  )}
                />
              </FormField>
            </StyledFormFieldsContainer>
          </StyledFormSectionContainer>
        </CardGrid>

        <CardGrid css={cardGridStyles} direction={Direction.ROW} title={t('form.request_details.title')}>
          <StyledFormSectionContainer>
            <Box css={cardLabelStyles}>
              <StyledParagraph>{t('form.request_details.subtitle')}</StyledParagraph>
            </Box>
            <FormField css={cardBoldLabelStyles}>{t('form.request_details.message')}</FormField>
            <FormField css={cardBoldLabelStyles}>{t('form.request_details.message_partner_consent')}</FormField>

            <StyledFormFieldsContainer>
              <FormField css={rightFormFieldStyles}>
                <Controller
                  control={control}
                  name="typeOfRequest"
                  render={({ field }) => {
                    const item = field.value as unknown as string;
                    return (
                      <SelectA11y
                        {...field}
                        value={item || ''}
                        defaultValue={item || ''}
                        error={!!errors.typeOfRequest}
                        errorDescription={errors.typeOfRequest?.message}
                        fullWidth
                        id={field.name}
                        items={typeOfRequestOptions}
                        label={t('form.request_details.type_of_request')}
                        listWidth="100%"
                        name={field.name}
                        placeholder={t('form.request_details.select_type_of_request')}
                        testId="withdrawal-form-input-type-of-request"
                        variant={Variants.PRIMARY}
                        secondaryLabel={t('form.required')}
                      />
                    );
                  }}
                />
              </FormField>
              <FormField css={rightFormFieldStyles}>
                <Controller
                  control={control}
                  name="hrnSampleStatus"
                  render={({ field }) => {
                    const item = field.value as unknown as string;
                    return (
                      <SelectA11y
                        {...field}
                        value={item || ''}
                        defaultValue={item || ''}
                        error={!!errors.hrnSampleStatus}
                        errorDescription={errors.hrnSampleStatus?.message}
                        fullWidth
                        id={field.name}
                        items={hrnSampleStatusOptions}
                        label={t('form.request_details.hrn_sample_status')}
                        listWidth="100%"
                        name={field.name}
                        placeholder={t('form.request_details.select_hrn_sample_status')}
                        testId="withdrawal-form-input-hrn-sample-status"
                        variant={Variants.PRIMARY}
                        secondaryLabel={t('form.required')}
                      />
                    );
                  }}
                />
              </FormField>
            </StyledFormFieldsContainer>

            <StyledFormFieldsContainer>
              <FormField css={rightFormFieldStyles}>
                <Controller
                  control={control}
                  name="consentMethod"
                  render={({ field }) => {
                    const item = field.value as unknown as string;
                    return (
                      <SelectA11y
                        {...field}
                        value={item || ''}
                        defaultValue={item || ''}
                        error={!!errors.consentMethod}
                        errorDescription={errors.consentMethod?.message}
                        fullWidth
                        id={field.name}
                        items={paperConsentOptions}
                        label={t('form.request_details.paper_consent')}
                        listWidth="100%"
                        name={field.name}
                        placeholder={t('form.request_details.select_paper_consent')}
                        testId="withdrawal-form-input-consent-method"
                        variant={Variants.PRIMARY}
                        secondaryLabel={t('form.required')}
                      />
                    );
                  }}
                />
              </FormField>
            </StyledFormFieldsContainer>

            <StyledFormFieldsContainer>
              <FormField css={rowFormFieldStyles}>
                <Controller
                  name="requestDetails"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextArea
                      {...field}
                      name={field.name}
                      id={field.name}
                      label={t('form.request_details.description')}
                      secondaryLabel={t('form.optional')}
                      variant={Variants.PRIMARY}
                      testId="withdrawal-form-input-request-details"
                      isSensitive={true}
                    />
                  )}
                />
              </FormField>
            </StyledFormFieldsContainer>
          </StyledFormSectionContainer>
        </CardGrid>

        <CardGrid css={cardGridStyles} direction={Direction.ROW} title={t('form.regulatory_documentation.title')}>
          <StyledFormSectionContainer>
            <Box css={cardLabelStyles}>
              <StyledParagraph>{t('form.regulatory_documentation.subtitle')}</StyledParagraph>
            </Box>

            <StyledFormFieldsContainer css={columnFormFieldStyles}>
              <FormField>
                <Controller
                  control={control}
                  name="documentationConfirmation"
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={documentationConfirmationCheck}
                      error={!!errors.documentationConfirmation}
                      errorDescription={errors.documentationConfirmation?.message}
                      id={field.name}
                      label={t('form.regulatory_documentation.documentation_confirmation')}
                      secondaryLabel={t('form.required')}
                      labelstyles={cardLabelStyles}
                      name={field.name}
                      onChange={() => {
                        setDocumentationConfirmationCheck(!documentationConfirmationCheck);
                        field.onChange(!documentationConfirmationCheck);
                      }}
                    />
                  )}
                />
              </FormField>
              <FormField>
                <Controller
                  control={control}
                  name="orderCancellationCheck"
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={orderCancellationCheck}
                      error={!!errors.orderCancellationCheck}
                      errorDescription={errors.orderCancellationCheck?.message}
                      id={field.name}
                      label={t('form.regulatory_documentation.order_cancellation_check')}
                      secondaryLabel={t('form.required')}
                      labelstyles={cardLabelStyles}
                      name={field.name}
                      onChange={() => {
                        setOrderCancellationCheck(!orderCancellationCheck);
                        field.onChange(!orderCancellationCheck);
                      }}
                    />
                  )}
                />
              </FormField>
            </StyledFormFieldsContainer>
          </StyledFormSectionContainer>
        </CardGrid>

        <CardGrid css={cardGridStyles} direction={Direction.ROW} title={t('form.reason_for_withdrawal.title')}>
          <StyledFormSectionContainer>
            <Box css={cardLabelStyles}>
              <StyledParagraph>{t('form.reason_for_withdrawal.subtitle')}</StyledParagraph>
            </Box>

            <StyledFormFieldsContainer css={columnFormFieldStyles}>
              {WithdrawalReasonOptions.map((item) => (
                <Controller
                  key={item.id}
                  control={control}
                  name={`reasonsForWithdrawal.${item.id}`}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={!!field.value || false}
                      id={item.name}
                      label={t(`form.reason_for_withdrawal.${item.id}`)}
                      labelstyles={cardLabelStyles}
                      name={item.name}
                      onChange={(e: any) => field.onChange(e)}
                    />
                  )}
                />
              ))}
            </StyledFormFieldsContainer>
          </StyledFormSectionContainer>
        </CardGrid>

        <Button
          aria-label="form.submit_information"
          loading={isLoading}
          css={submitButtonStyles}
          data-testid="withdrawal-form-submit-button"
          disabled={!!isLoading}
          size={Sizes.LARGE}
          type="submit"
        >
          {t('form.submit_information')}
        </Button>
      </Form>
    </>
  );
};

export default WithdrawalForm;
